import React, { useCallback, useEffect, useState } from 'react';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { Alert, Button, DialogV2, TextField } from 'c-components';
import type { CampaignInsertionOrderPlaceholder } from 'c-sdk';
import { useCommonTranslation } from 'c-translation';
import { parseImpressionsTargetInput } from './impressionsTargetInput';

/**
 * UL-8136 -- adding and deleting planned lines (placeholders) on the DSP schedule page.
 */

type AddProps = {
    open: boolean;
    onClose: () => void;
    /** Resolves true when saved; the dialog then closes. */
    onSave: (label: string, target: number | null) => Promise<boolean>;
    busy: boolean;
    error: string | null;
};

/**
 * Label (required) and Impressions target (optional, the same rules as the table's target cell).
 * A new planned line goes on the campaign Default.
 */
export const AddPlaceholderDialog: React.FC<AddProps> = ({
    open,
    onClose,
    onSave,
    busy,
    error,
}) => {
    const t = useCommonTranslation();
    const [label, setLabel] = useState('');
    const [target, setTarget] = useState('');

    useEffect(() => {
        if (!open) return;
        setLabel('');
        setTarget('');
    }, [open]);

    const parsedTarget = parseImpressionsTargetInput(target, null);
    const canSave = label.trim() !== '' && !parsedTarget.invalid && !busy;

    const save = useCallback(async () => {
        if (!canSave) return;
        if (await onSave(label.trim(), parsedTarget.parsed)) onClose();
    }, [canSave, label, onClose, onSave, parsedTarget.parsed]);

    return (
        <DialogV2
            open={open}
            onClose={onClose}
            maxWidth="xs"
            title="Modules.Main.Campaigns.CampaignSchedule.dsp.placeholders.add"
        >
            <Stack gap={2} pt={1}>
                <TextField
                    label={t('Modules.Main.Campaigns.CampaignSchedule.dsp.placeholders.label')}
                    value={label}
                    required
                    onChange={e => setLabel(e.target.value)}
                    fullWidth
                />
                <TextField
                    label={t(
                        'Modules.Main.Campaigns.CampaignSchedule.dsp.columns.impressionsTarget',
                    )}
                    value={target}
                    error={parsedTarget.invalid}
                    helperText={
                        parsedTarget.invalid
                            ? t(
                                  'Modules.Main.Campaigns.CampaignSchedule.dsp.impressionsTarget.invalid',
                              )
                            : undefined
                    }
                    onChange={e => setTarget(e.target.value)}
                    inputProps={{ inputMode: 'decimal' }}
                    fullWidth
                />
                {error && <Alert severity="error">{error}</Alert>}
                <Stack direction="row" justifyContent="flex-end" gap={1}>
                    <Button variant="text" onClick={onClose} disabled={busy}>
                        {t('CommonWords.Cancel')}
                    </Button>
                    <Button onClick={save} disabled={!canSave}>
                        {busy ? <CircularProgress size={20} /> : t('CommonWords.Save')}
                    </Button>
                </Stack>
            </Stack>
        </DialogV2>
    );
};

type DeleteProps = {
    placeholder: CampaignInsertionOrderPlaceholder | null;
    onClose: () => void;
    onConfirm: (placeholder: CampaignInsertionOrderPlaceholder) => Promise<boolean>;
    busy: boolean;
    error: string | null;
};

/** Deleting a planned line takes its target off the campaign, so it asks first. */
export const DeletePlaceholderDialog: React.FC<DeleteProps> = ({
    placeholder,
    onClose,
    onConfirm,
    busy,
    error,
}) => {
    const t = useCommonTranslation();

    const confirm = useCallback(async () => {
        if (placeholder && (await onConfirm(placeholder))) onClose();
    }, [onClose, onConfirm, placeholder]);

    return (
        <DialogV2
            open={placeholder != null}
            onClose={onClose}
            maxWidth="sm"
            title="Modules.Main.Campaigns.CampaignSchedule.dsp.placeholders.delete"
        >
            <Stack gap={2}>
                <Typography variant="body1" fontWeight={600}>
                    {placeholder?.label}
                </Typography>
                <Alert severity="warning" variant="outlined">
                    {t('Modules.Main.Campaigns.CampaignSchedule.dsp.placeholders.deleteConfirm')}
                </Alert>
                {error && <Alert severity="error">{error}</Alert>}
                <Stack direction="row" justifyContent="flex-end" gap={1}>
                    <Button variant="text" onClick={onClose} disabled={busy}>
                        {t('CommonWords.Cancel')}
                    </Button>
                    <Button color="error" onClick={confirm} disabled={busy}>
                        {busy ? (
                            <CircularProgress size={20} />
                        ) : (
                            t('Modules.Main.Campaigns.CampaignSchedule.dsp.placeholders.delete')
                        )}
                    </Button>
                </Stack>
            </Stack>
        </DialogV2>
    );
};
